<template>
  <div class="price-range-step">
    <div class="price-range-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Form Fields -->
      <div class="form-fields">
        <!-- Currency Select -->
        <div class="field-group">
          <label class="field-label">Валюта</label>
          <Select
            v-model="selectedCurrency"
            :options="step.currencyOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Выберите валюту"
            class="field-select"
            @update:model-value="updateCurrency"
          />
        </div>

        <!-- Price Range Radio Buttons -->
        <div v-if="selectedCurrency" class="field-group">
          <label class="field-label">Ценовой диапазон</label>
          <div class="radio-group">
            <div
              v-for="option in currentPriceRanges"
              :key="option.value"
              class="radio-option"
            >
              <RadioButton
                :inputId="`price-${option.value}`"
                v-model="selectedPriceRange"
                :value="option.value"
                @update:model-value="updatePriceRange"
              />
              <label :for="`price-${option.value}`" class="radio-label">
                {{ option.label }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import Select from 'primevue/select'
import RadioButton from 'primevue/radiobutton'
import { useGiftModal } from '@/features/gift/store'
import type { GiftStep, PriceRangeOption } from '@/features/gift/types/gift-config'

interface Props {
  step: GiftStep & { type: 'price_range' }
}

const props = defineProps<Props>()
const giftStore = useGiftModal()

const selectedCurrency = ref<string | null>(null)
const selectedPriceRange = ref<string | null>(null)

// Вычисляем доступные ценовые диапазоны для выбранной валюты
const currentPriceRanges = computed<PriceRangeOption[]>(() => {
  if (!selectedCurrency.value) {
    return []
  }
  return props.step.priceRangesByCurrency[selectedCurrency.value] || []
})

function updateCurrency() {
  giftStore.updateGiftData(`${props.step.id}_currency`, selectedCurrency.value)
  // При смене валюты сбрасываем выбранный ценовой диапазон
  selectedPriceRange.value = null
  giftStore.updateGiftData(`${props.step.id}_priceRange`, null)
}

function updatePriceRange() {
  giftStore.updateGiftData(`${props.step.id}_priceRange`, selectedPriceRange.value)
}

onMounted(() => {
  const data = giftStore.giftModal.giftData
  selectedCurrency.value = data[`${props.step.id}_currency`] || null
  selectedPriceRange.value = data[`${props.step.id}_priceRange`] || null
})
</script>

<style scoped>
.price-range-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.price-range-content {
  max-width: 500px;
  width: 100%;
}

.title-section {
  margin-bottom: 32px;
  text-align: left;
}

.step-title {
  font-family: 'Gilroy';
  font-size: 32px;
  line-height: 1.09;
  font-weight: 700;
  letter-spacing: -0.64px;
  color: var(--primary-blue-100);
  margin: 0 0 12px 0;
}

.step-subtitle {
  font-family: 'Inter';
  font-size: 15px;
  color: var(--primary-blue-80);
  line-height: 1.3;
  letter-spacing: -0.64px;
  margin: 0;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field-label {
  font-size: 14px;
  font-weight: 500;
  color: #1d1d1f;
}

.field-select {
  width: 100%;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 12px;
}

.radio-label {
  font-size: 16px;
  color: #1d1d1f;
  cursor: pointer;
}

@media (min-width: 1024px) {
  .title-section {
    text-align: left;
    gap: 8px;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    letter-spacing: -0.04em;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
    letter-spacing: -0.04em;
  }
}
</style>

